import { BIDDING_TYPES, toApiCalendarDate } from "../../utils";
import { OrderToSubmitRequest } from "../../types";
import { buildOrderBodyCore } from "../Order/orderPayloadCore";
import { isQuotedByLine } from "../Order/serviceDetail";

/**
 * SINGLE SOURCE OF TRUTH for the launch-from-PR create/edit payload
 * (`POST /order`, `POST /order/multiple`, and `PUT /order/{id}` when an entry
 * carries `orderId`). Extracted CHARACTER-FOR-CHARACTER from the pre-Carbon
 * `OrderByRequest.tsx#onSubmit` so the request body stays BYTE-EQUIVALENT through
 * the Carbon migration (T6, ruling: CREATE CONTRACT).
 *
 * NOTE: this is deliberately NOT `Order/buildOrderPayload`. The launch-from-PR
 * body carries extra fields the RFQ-builder body does not (`id` for the edit
 * path, `user`, `company`, `multipleOrder`, `itemFiles`,
 * `purchaseRequestId`, `purchaseRequestItemId`) and differs in three field
 * derivations (`quantity` defaults to 1; `internalCode` prefers
 * `purchaseRequestItemId`; `initialPrice`/`minimumDecrement` are gated on a
 * truthy line value). Reusing Order/buildOrderPayload here would silently change
 * the wire contract — so the two builders stay separate on purpose.
 */
export type BuildRequestOrderPayloadArgs = {
  ordersToSubmit: OrderToSubmitRequest[];
  generalBidData: any;
  selectedType: { value: string };
  selectedComplexType: { value: string };
  user:
    | {
        id?: string;
        email?: string;
        fullName?: string;
        company?: { id?: string };
      }
    | null
    | undefined;
};

export const buildRequestOrderPayload = ({
  ordersToSubmit,
  generalBidData,
  selectedType,
  selectedComplexType,
  user,
}: BuildRequestOrderPayloadArgs) =>
  ordersToSubmit.map((mat: OrderToSubmitRequest) => ({
    id: mat.orderId,
    ...buildOrderBodyCore(mat, generalBidData, {
      selectedType,
      selectedComplexType,
      user,
    }),
    // ── launch-from-PR-specific derivations & extra fields ──────────────────
    quantity:
      !isQuotedByLine(mat) && mat.quantity ? parseFloat(String(mat.quantity)) : 1,
    user: { id: user?.id },
    company: { id: user?.company?.id },
    multipleOrder: mat.multipleOrderId ? { id: mat.multipleOrderId } : null,
    itemFiles: (mat as any).itemFiles ?? [],
    // The per-item delivery date UI was removed for joint quotations; fall back
    // to the solped's needed dates (seeded) or the bidding window. Noon-anchored
    // (see toApiCalendarDate) so the backend does not roll the day back a day.
    startSupplyDate:
      mat?.startSupplyDate || generalBidData.startDateValue
        ? toApiCalendarDate(mat?.startSupplyDate || generalBidData.startDateValue)
        : undefined,
    endSupplyDate:
      mat?.endSupplyDate || generalBidData.endDateValue
        ? toApiCalendarDate(mat?.endSupplyDate || generalBidData.endDateValue)
        : undefined,
    internalCode: mat?.purchaseRequestItemId || generalBidData.internalCode,
    initialPrice:
      selectedType.value === BIDDING_TYPES.INVERSE && mat.initialPrice
        ? parseFloat(mat.initialPrice)
        : null,
    minimumDecrement:
      selectedType.value === BIDDING_TYPES.INVERSE && mat.minimumDecrement
        ? parseFloat(mat.minimumDecrement)
        : null,
    purchaseRequestId: mat?.purchaseRequestId,
    purchaseRequestItemId: mat?.purchaseRequestItemId,
  }));
